// web/src/components/ds/ContextMenuHost.tsx — DS.5 (sprint aos-ds-shell, spec §2 D3/D4).
//
// A context menu (right-click / long-press) on the DS `Menu` piece, which
// only knows how to open from a node `trigger` (§0.15: "no contextmenu
// binding"). The host renders a `position: fixed`, zero-size anchor at the
// pointer coordinates with an invisible trigger inside and drives the menu
// open through DsMenu's controlled mode. Two properties fall out of the
// fixed anchor and are why it is one, not an inline wrapper:
//   * it escapes every ancestor overflow box (the sidebar column is
//     `overflow: hidden`), so the menu is never clipped — the class of
//     defect D4's clipping leg exists for;
//   * it sits at the skin's `--z-scrim` rung inside `.aos-ds-root`, above the
//     chat column's own positioned content.
// The DS piece then does the rest: outside-mousedown and Escape close it,
// the vertical `clipAncestor()` flip keeps it in the viewport, and DsMenu
// moves focus in so the keyboard works from the first keypress.
//
// Callers: AgentContextMenu (desktop branch) and WorkspaceAvatarContextMenu.
// The mobile bottom sheet stays on shadcn `Sheet` (DS.7 owns it).

import { DsMenu, type DsMenuItem } from './DsMenu';
import './ds-adapters.css';

export interface ContextMenuHostProps {
  open: boolean;
  /** Viewport coordinates of the pointer (null → top-left, the mobile fallback). */
  position: { x: number; y: number } | null;
  onClose: () => void;
  items: DsMenuItem[];
  menuTestId?: string;
  dividerTestId?: string;
}

export function ContextMenuHost({
  open,
  position,
  onClose,
  items,
  menuTestId,
  dividerTestId,
}: ContextMenuHostProps) {
  if (!open) return null;
  return (
    <div
      className="fixed aos-ds-anchor"
      style={{
        left: position?.x ?? 0,
        top: position?.y ?? 0,
        width: 0,
        height: 0,
        zIndex: 'var(--z-scrim)',
      }}
      data-context-menu-anchor=""
    >
      <DsMenu
        open
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        trigger={<span aria-hidden="true" />}
        items={items}
        data-testid={menuTestId}
        dividerTestId={dividerTestId}
      />
    </div>
  );
}

export default ContextMenuHost;
